import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';

/**
 * Password Strength Meter Component
 * Shows real-time password strength feedback during registration
 */
export const PasswordStrengthMeter = ({ password }) => {
  const analysis = useMemo(() => {
    if (!password) {
      return { score: 0, label: '', color: '', requirements: [] };
    }

    // Length-first guidance (no composition rules); the server also blocks common passwords.
    const len = password.length;
    const requirements = [
      { label: 'At least 10 characters', met: len >= 10 },
      { label: 'Not an obvious password or simple pattern', met: len >= 10 && new Set(password).size > 3 && !/^(password|qwerty|123456|letmein|welcome|admin)/i.test(password) },
      { label: '16+ characters or several words is even better', met: len >= 16 },
    ];
    const score = len === 0 ? 0 : len < 10 ? 25 : len < 13 ? 50 : len < 16 ? 75 : 100;
    const [label, color] = score <= 25 ? ['Too short', 'bg-red-500'] : score <= 50 ? ['Fair', 'bg-orange-500'] : score <= 75 ? ['Good', 'bg-yellow-500'] : ['Strong', 'bg-green-500'];

    return { score, label, color, requirements };
  }, [password]);

  if (!password) {
    return null;
  }

  return (
    <div className="mt-2 space-y-2" data-testid="password-strength-meter">
      {/* Strength Bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${analysis.color}`}
            style={{ width: `${analysis.score}%` }}
          />
        </div>
        {analysis.label && (
          <span className={`text-xs font-medium ${
            analysis.score <= 25 ? 'text-red-600' :
            analysis.score <= 50 ? 'text-orange-600' :
            analysis.score <= 75 ? 'text-yellow-600' :
            'text-green-600'
          }`}>
            {analysis.label}
          </span>
        )}
      </div>

      {/* Requirements Checklist */}
      <div className="grid grid-cols-1 gap-1">
        {analysis.requirements.map((req, index) => (
          <div
            key={index}
            className={`flex items-center gap-1.5 text-xs ${
              req.met ? 'text-green-600' : 'text-neutral-400'
            }`}
          >
            {req.met ? (
              <Check className="w-3 h-3" />
            ) : (
              <X className="w-3 h-3" />
            )}
            <span>{req.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordStrengthMeter;
